'use client'

import { CheckMark } from '@qr-front/icons/checkmark'
import { Button } from '@qr-front/ui/components/Button'
import { Dialog, DialogContent } from '@qr-front/ui/components/Dialog'
import { useAtom, useAtomValue } from 'jotai'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import { ROUTES } from '@/constants'
import { qrDownloadSuccessDialogAtom } from '@/generator/client'
import { userAtom } from '@/global-state'

const LazySuccessIllustration = React.lazy<
	React.ComponentType<{ className?: string }>
>(async () => {
	try {
		const illustrationModule = await import('./SuccessIllustration')
		return { default: illustrationModule.SuccessIllustration }
	} catch {
		return { default: () => null }
	}
})

export const QrDownloadSuccessDialog = () => {
	const t = useTranslations()
	const router = useRouter()

	const user = useAtomValue(userAtom)
	const [isOpen, setIsOpen] = useAtom(qrDownloadSuccessDialogAtom)

	const onOpenChange = () => {
		setIsOpen(false)
	}

	const handleManageQr = () => {
		setIsOpen(false)
		router.push(ROUTES.cabinet.qrCodes)
	}

	const handleTrackAnalytics = () => {
		setIsOpen(false)
		router.push(ROUTES.cabinet.analytics)
	}

	if (!isOpen || !user) return null

	return (
		<Dialog open={isOpen} onOpenChange={onOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="w-[350px] p-0 tablet:w-[500px]"
			>
				<Suspense
					fallback={
						<div className="bg-gradient-to-r from-gray-100 to-gray-200 hidden h-32 w-full animate-pulse rounded-12 tablet:block" />
					}
				>
					<LazySuccessIllustration className="hidden w-full tablet:block" />
				</Suspense>
				<div className="p-6 desktop:p-8">
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-2">
							<h3 className="type-heading-3 text-black">
								{t('public__qr__download__success__title')}
							</h3>
							<p className="type-body-medium text-dark-grey">
								{t('public__qr__download__success__subtitle')}
							</p>
						</div>

						<ul className="type-body-small flex w-full flex-col gap-3 rounded-12 border border-border-grey/50 p-6 text-dark-grey">
							{t.rich('public__qr__download__success__description', {
								li: (chunks) => (
									<li className="flex items-center gap-4 [word-break:break-word]">
										<CheckMark className="min-h-4 min-w-4 shrink-0 text-blue" />
										{chunks}
									</li>
								),
							})}
						</ul>

						<div className="flex w-full flex-col gap-4 tablet:flex-row tablet:gap-6">
							<Button
								visual="default"
								size="large"
								className="type-body-large w-full focus-visible:outline-none tablet:w-1/2"
								onClick={handleManageQr}
							>
								{t('public__qr__download__success__manage_button')}
							</Button>
							<Button
								visual="secondary"
								size="large"
								className="type-body-large w-full focus-visible:outline-none tablet:w-1/2"
								onClick={handleTrackAnalytics}
							>
								{t('public__qr__download__success__analytics_button')}
							</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
